import type { Posicao } from '@wms/shared';
import { compararNatural } from './utils';

/**
 * Geometria do CD em dois níveis, como o blueprint pede.
 *
 * 1. PLANTA  — um corredor por faixa, agregado. É a visão "de cima".
 * 2. ELEVAÇÃO — o rack desenhado: X = `moduloOrdem`, Y = `nivelRack`.
 *
 * A elevação só é possível porque BOX codifica a altura da prateleira
 * (001/1 = piso … 701 = nível 7) — a API já entrega isso derivado em
 * `nivelRack`/`moduloOrdem`. Onde `geometriaConfiavel` é `false`, a posição
 * NÃO é desenhada no rack: afirmar altura errada é pior que não desenhar.
 *
 * Corredor é normalizado para maiúsculas ao agrupar: o cadastro tem `MC005`
 * e `mc005` como se fossem ruas diferentes, e não são.
 */

export const CELULA = 14;
export const ESPACO = 2;
export const PASSO = CELULA + ESPACO;
export const LARGURA_ROTULO = 58;
const ALTURA_TITULO = 26;
const VAO_ELEVACAO = 34;
const MARGEM = 14;

export function normalizarCorredor(corredor: string): string {
  return corredor.trim().toUpperCase() || '(sem corredor)';
}

// --- Planta ----------------------------------------------------------------

export interface FaixaPlanta {
  corredor: string;
  zona: string;
  total: number;
  ocupadas: number;
  livres: number;
  negativos: number;
  percentual: number;
  modulos: number;
  niveis: number;
  /** `false` se qualquer posição do corredor foge do padrão de endereço. */
  geometriaConfiavel: boolean;
}

export function montarPlanta(posicoes: readonly Posicao[]): FaixaPlanta[] {
  const mapa = new Map<
    string,
    {
      zona: string;
      total: number;
      ocupadas: number;
      livres: number;
      negativos: number;
      modulos: Set<number>;
      niveis: Set<number>;
      confiavel: boolean;
    }
  >();

  for (const posicao of posicoes) {
    const chave = normalizarCorredor(posicao.corredor);
    let faixa = mapa.get(chave);
    if (!faixa) {
      faixa = {
        zona: posicao.zona,
        total: 0,
        ocupadas: 0,
        livres: 0,
        negativos: 0,
        modulos: new Set<number>(),
        niveis: new Set<number>(),
        confiavel: true,
      };
      mapa.set(chave, faixa);
    }
    faixa.total += 1;
    if (posicao.status === 'ocupada') faixa.ocupadas += 1;
    if (posicao.status === 'livre') faixa.livres += 1;
    if (posicao.status === 'divergencia') faixa.negativos += 1;
    faixa.modulos.add(posicao.moduloOrdem);
    if (posicao.geometriaConfiavel) faixa.niveis.add(posicao.nivelRack);
    else faixa.confiavel = false;
  }

  return [...mapa.entries()]
    .map(([corredor, v]) => ({
      corredor,
      zona: v.zona,
      total: v.total,
      ocupadas: v.ocupadas,
      livres: v.livres,
      negativos: v.negativos,
      percentual: v.total > 0 ? (v.ocupadas / v.total) * 100 : 0,
      modulos: v.modulos.size,
      niveis: v.niveis.size,
      geometriaConfiavel: v.confiavel,
    }))
    // Maior rua primeiro: é onde está a operação (e o blueprint desenha assim).
    .sort((a, b) => b.total - a.total || compararNatural(a.corredor, b.corredor));
}

// --- Elevação --------------------------------------------------------------

export interface CelulaElevacao {
  readonly posicao: Posicao;
  readonly x: number;
  readonly y: number;
  readonly l: number;
  readonly a: number;
  readonly nivel: number;
}

export interface Elevacao {
  readonly corredor: string;
  readonly x: number;
  readonly y: number;
  readonly largura: number;
  readonly altura: number;
  /** Topo do bloco, incluindo a faixa de título. */
  readonly topo: number;
  readonly celulas: CelulaElevacao[];
  /** Níveis desenhados, do mais alto para o piso. */
  readonly niveis: number[];
  /** Níveis sem nenhuma posição ocupada — oportunidade de consolidação. */
  readonly niveisVazios: Set<number>;
  readonly modulos: number[];
  readonly total: number;
  readonly ocupadas: number;
  readonly negativos: number;
  /** Posições que não seguem o padrão de endereço e por isso não são desenhadas. */
  readonly semGeometria: Posicao[];
}

export interface LayoutElevacao {
  readonly elevacoes: Elevacao[];
  readonly porLocalizador: Map<number, CelulaElevacao>;
  readonly largura: number;
  readonly altura: number;
  /** Total de células desenháveis — usado para decidir virtualização. */
  readonly celulas: number;
}

export function rotuloNivel(nivel: number): string {
  return nivel === 0 ? 'piso' : `niv${nivel}`;
}

export function montarElevacao(
  posicoes: readonly Posicao[],
  corredores: readonly string[],
): LayoutElevacao {
  const selecionados = new Set(corredores.map(normalizarCorredor));
  const porCorredor = new Map<string, Posicao[]>();

  for (const posicao of posicoes) {
    const chave = normalizarCorredor(posicao.corredor);
    if (selecionados.size > 0 && !selecionados.has(chave)) continue;
    const lista = porCorredor.get(chave);
    if (lista) lista.push(posicao);
    else porCorredor.set(chave, [posicao]);
  }

  const elevacoes: Elevacao[] = [];
  const porLocalizador = new Map<number, CelulaElevacao>();
  let y = MARGEM;
  let larguraMaxima = 0;
  let celulas = 0;

  const ordem = [...porCorredor.keys()].sort(compararNatural);

  for (const corredor of ordem) {
    const doCorredor = porCorredor.get(corredor) ?? [];
    const desenhaveis = doCorredor.filter((p) => p.geometriaConfiavel);
    const semGeometria = doCorredor.filter((p) => !p.geometriaConfiavel);

    const modulos = [...new Set(desenhaveis.map((p) => p.moduloOrdem))].sort((a, b) => a - b);
    const niveis = [...new Set(desenhaveis.map((p) => p.nivelRack))].sort((a, b) => b - a);
    const colunaDoModulo = new Map(modulos.map((m, i) => [m, i]));
    const linhaDoNivel = new Map(niveis.map((n, i) => [n, i]));

    const ocupadasPorNivel = new Map<number, number>();
    const listaCelulas: CelulaElevacao[] = [];
    const topo = y;
    const inicioGrade = y + ALTURA_TITULO;

    for (const posicao of desenhaveis) {
      const coluna = colunaDoModulo.get(posicao.moduloOrdem) ?? 0;
      const linha = linhaDoNivel.get(posicao.nivelRack) ?? 0;
      const celula: CelulaElevacao = {
        posicao,
        x: MARGEM + LARGURA_ROTULO + coluna * PASSO,
        y: inicioGrade + linha * PASSO,
        l: CELULA,
        a: CELULA,
        nivel: posicao.nivelRack,
      };
      listaCelulas.push(celula);
      porLocalizador.set(posicao.localizador, celula);
      if (posicao.status === 'ocupada') {
        ocupadasPorNivel.set(posicao.nivelRack, (ocupadasPorNivel.get(posicao.nivelRack) ?? 0) + 1);
      }
    }

    const niveisVazios = new Set(niveis.filter((n) => (ocupadasPorNivel.get(n) ?? 0) === 0));
    const largura = LARGURA_ROTULO + Math.max(modulos.length, 1) * PASSO;
    const altura = Math.max(niveis.length, 1) * PASSO - ESPACO;

    elevacoes.push({
      corredor,
      x: MARGEM,
      y: inicioGrade,
      topo,
      largura,
      altura,
      celulas: listaCelulas,
      niveis,
      niveisVazios,
      modulos,
      total: doCorredor.length,
      ocupadas: doCorredor.filter((p) => p.status === 'ocupada').length,
      negativos: doCorredor.filter((p) => p.status === 'divergencia').length,
      semGeometria,
    });

    celulas += listaCelulas.length;
    larguraMaxima = Math.max(larguraMaxima, largura);
    y = inicioGrade + altura + VAO_ELEVACAO;
  }

  return {
    elevacoes,
    porLocalizador,
    largura: larguraMaxima + MARGEM * 2,
    altura: Math.max(y - VAO_ELEVACAO + MARGEM, 1),
    celulas,
  };
}

/** Elevações que cruzam a janela visível. */
export function elevacoesVisiveis(
  layout: LayoutElevacao,
  topo: number,
  base: number,
): Elevacao[] {
  return layout.elevacoes.filter((e) => e.y + e.altura >= topo && e.topo <= base);
}

/** Célula sob o ponto, em coordenadas de mundo. */
export function celulaEm(layout: LayoutElevacao, x: number, y: number): CelulaElevacao | null {
  for (const elevacao of layout.elevacoes) {
    if (y < elevacao.y || y > elevacao.y + elevacao.altura) continue;
    for (const celula of elevacao.celulas) {
      if (
        x >= celula.x &&
        x <= celula.x + celula.l &&
        y >= celula.y &&
        y <= celula.y + celula.a
      ) {
        return celula;
      }
    }
    return null;
  }
  return null;
}
